import { handleError } from '@anchorage/sentry';
import { withScope } from '@sentry/nextjs';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

// Matches a Next.js dynamic route segment that was never interpolated, e.g.
// the "[roleID]" in "/apis/edit-role/[roleID]". A client-side navigation to
// such a URL means a caller pushed a route *pattern* (router.pathname / a route
// constant / an object-form push missing its query key) instead of a resolved
// path. That is the producer behind FRONTOFFICE-18A / CEXP-20068, which we have
// not been able to find statically.
const UNINTERPOLATED_SEGMENT = /\/\[[^/\]]+\]/;

/**
 * Reports client-side navigations to un-interpolated dynamic routes.
 *
 * Mounted once at the app root. `routeChangeStart` fires synchronously inside
 * router.push, on the *originating* page before the transition — so the thrown
 * Error's own stacktrace carries the call site that issued the navigation
 * (which `document.referrer` cannot), and window.location is still the source
 * page. Hard loads of a literal "[...]" URL do not emit `routeChangeStart` —
 * those are still caught by the per-page reporters.
 */
export function useReportUninterpolatedNavigation() {
  const router = useRouter();

  useEffect(() => {
    const handleRouteChangeStart = (targetUrl: string) => {
      if (!UNINTERPOLATED_SEGMENT.test(targetUrl)) {
        return;
      }

      // fromRoute is a path ("/apis/"), which the beforeSend `sanitizeEvent`
      // allowlist redacts out of `extra` (slashes aren't identifier-shaped).
      // Tags are not sanitized, so set it scoped to this event — withScope
      // keeps it off every subsequent event.
      withScope((scope) => {
        // routeChangeStart fires before the URL changes, so the current
        // location is the page that triggered the navigation.
        scope.setTag('fromRoute', window.location.pathname);
        handleError(
          new Error(`Navigation to un-interpolated route: ${targetUrl}`),
          {
            neverThrowError: true,
            extraContext: {
              targetUrl,
              historyLength: window.history.length,
            },
          },
        );
      });
    };

    router.events.on('routeChangeStart', handleRouteChangeStart);
    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
    };
  }, [router.events]);
}
